import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, LogOut, RotateCcw, CheckCheck } from "lucide-react";
import { previewStore, buildPreviewCourseBundle } from "./previewStore";

/**
 * ADMIN PREVIEW — LEARNER VIEW banner. Fixed to top of the page.
 * Only rendered when previewStore.isActive() is true.
 *
 * onSyncPreview: optional callback the host page provides to re-read preview
 * state after Reset / Mark-all-complete quick actions.
 */
export default function PreviewBanner({ course, onSyncPreview, forceHideCompleteAll = false, resetTo = "/academy/portal/dashboard" }) {
  const nav = useNavigate();
  const [tick, setTick] = useState(0);

  const exit = useCallback(() => {
    previewStore.exit();
    // Return the admin to Academy Portal admin area, keeping their admin session.
    nav("/care-portal/admin/academy-portal");
  }, [nav]);

  const reset = useCallback(() => {
    previewStore.reset();
    setTick(t => t + 1);
    onSyncPreview?.();
    // If the current page depends on preview state (e.g., certificate page),
    // navigate back to dashboard to reflect the reset cleanly.
    nav(resetTo);
  }, [nav, onSyncPreview, resetTo]);

  const markAll = useCallback(() => {
    if (!course) return;
    const ids = ["welcome", ...course.modules.map(m => m.id)];
    previewStore.markAllComplete(ids);
    setTick(t => t + 1);
    onSyncPreview?.();
  }, [course, onSyncPreview]);

  return (
    <>
      {/* Reserve space so the banner never covers real UI. */}
      <div className="h-14" aria-hidden="true" />
      <div
        data-testid="preview-banner"
        data-preview-tick={tick}
        className="fixed top-0 inset-x-0 z-[60] bg-[#0F172A] text-white border-b-2 border-[#B8935A] shadow-lg"
        role="region"
        aria-label="Admin preview banner"
      >
        <div className="max-w-6xl mx-auto px-3 sm:px-6 h-14 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[#B8935A] text-[#0F172A] text-[10px] font-bold tracking-[0.14em]">
              <Eye className="w-3 h-3" />
              ADMIN PREVIEW
            </span>
            <span className="hidden sm:inline text-[11px] uppercase tracking-[0.2em] text-slate-200 font-semibold">
              LEARNER VIEW
            </span>
            <span className="hidden md:inline text-xs text-slate-400 truncate">
              Nothing here writes to production. Preview state is client-side only.
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!forceHideCompleteAll && course && (
              <button
                onClick={markAll}
                data-testid="preview-mark-complete"
                title="Mark all modules + welcome complete for preview only"
                className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-500 text-[11px] text-slate-100 hover:bg-slate-800"
              >
                <CheckCheck className="w-3 h-3" /> Mark all
              </button>
            )}
            <button
              onClick={reset}
              data-testid="preview-reset"
              title="Reset preview progress"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-500 text-[11px] text-slate-100 hover:bg-slate-800"
            >
              <RotateCcw className="w-3 h-3" /> Reset preview
            </button>
            <button
              onClick={exit}
              data-testid="preview-exit"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#B8935A] hover:bg-[#a37e46] text-[#0F172A] text-[11px] font-bold tracking-wide"
            >
              <LogOut className="w-3 h-3" /> EXIT PREVIEW
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export { buildPreviewCourseBundle };
